import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Building2, ArrowRight, Loader2, Users, CalendarDays, BarChart3, Brain } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', full_name: '', organization_name: '' });
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        await register(form);
        toast.success('Registrazione completata!');
        navigate('/onboarding');
      } else {
        await login(form.email, form.password);
        toast.success('Benvenuto!');
        navigate('/home');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante l\'autenticazione');
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setForm({ ...form, email: 'demo@test.com', password: 'demo123' });
    setIsRegister(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1A3A5C] relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 border border-white/30 rounded-full" />
          <div className="absolute bottom-32 right-16 w-96 h-96 border border-white/20 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-48 h-48 border border-white/25 rounded-full" />
        </div>
        <div className="relative z-10 max-w-md px-8">
          <Building2 className="w-12 h-12 text-[#2E75B6] mb-6" />
          <h1 className="text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            HumanAImpact
          </h1>
          <p className="text-lg text-white/80 leading-relaxed mb-2">
            La piattaforma operativa per professionisti del coaching e della formazione.
          </p>
          <p className="text-sm text-white/60 leading-relaxed">
            Gestisci contatti, eventi, prodotti e comunicazioni da un unico punto di controllo. L'intelligenza artificiale lavora insieme a te per personalizzare ogni interazione.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-4 text-sm text-white/60">
            <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#2E75B6]" />CRM relazionale</div>
            <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#2E75B6]" />Gestione eventi</div>
            <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-[#2E75B6]" />Assistenti AI integrati</div>
            <div className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#2E75B6]" />Analytics e pipeline</div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F5F5F5]">
        <Card className="w-full max-w-md shadow-lg border-[#E2E8F0]">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex justify-center mb-4">
              <Building2 className="w-10 h-10 text-[#1A3A5C]" />
            </div>
            <CardTitle className="text-2xl text-[#1A3A5C]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {isRegister ? 'Crea il tuo account' : 'Accedi alla piattaforma'}
            </CardTitle>
            <CardDescription className="text-[#64748B]">
              {isRegister ? 'Inizia a organizzare il tuo lavoro con AI' : 'Inserisci le tue credenziali per continuare'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Nome completo</Label>
                    <Input id="full_name" value={form.full_name} onChange={set('full_name')} placeholder="Mario Rossi" required data-testid="register-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="org_name">Nome organizzazione</Label>
                    <Input id="org_name" value={form.organization_name} onChange={set('organization_name')} placeholder="La mia azienda" data-testid="register-org-input" />
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={set('email')} placeholder="email@esempio.com" required data-testid="login-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={form.password} onChange={set('password')} placeholder="La tua password" required minLength={6} data-testid="login-password-input" />
              </div>
              <Button type="submit" className="w-full bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" disabled={loading} data-testid="login-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                {isRegister ? 'Registrati' : 'Accedi'}
              </Button>
            </form>

            {/* Demo credentials */}
            {!isRegister && (
              <div className="mt-4 p-3 bg-[#F0F7FF] border border-[#2E75B6]/20 rounded-lg">
                <p className="text-xs font-medium text-[#1A3A5C] mb-1.5">Accesso demo per sviluppo</p>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-[#64748B] space-y-0.5">
                    <p>Email: <span className="font-mono text-[#1A3A5C]">demo@test.com</span></p>
                    <p>Password: <span className="font-mono text-[#1A3A5C]">demo123</span></p>
                  </div>
                  <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-[#2E75B6]/30 text-[#2E75B6]"
                    onClick={fillDemo} data-testid="fill-demo-btn">
                    Compila
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setIsRegister(!isRegister)}
                className="text-sm text-[#2E75B6] hover:underline font-medium"
                data-testid="toggle-auth-mode"
              >
                {isRegister ? 'Hai gia un account? Accedi' : 'Non hai un account? Registrati'}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
